import React, { ReactNode, FC, useMemo, useContext, ComponentType } from 'react';
import { ThemeContext } from 'styled-components';

import findChildByType from '../../../utils/components/#findChildByType';
import excludeChildrenByType from '../../../utils/components/#excludeChildrenByType';

import { ReactComponent as NotInterestedIcon } from '../../../icons/notInterested.svg';

import { StyledMessage, StyledMessageList, StyledMessageItem, StyledActions, StyledText } from './ErrorMessage.styles';
import { Content, Action } from './ErrorMessage.compounds';
import { MessageProps } from '../Message';

export interface ErrorMessageProps extends Omit<MessageProps, 'children'> {
  /** Error message. If an object, it should contain a `message` prop. */
  error?: ReactNode | { message: ReactNode } | ReactNode[];
  /** The error message title. */
  errorTitle?: string | ReactNode;
  /** Use the `forbidden` icon */
  forbidden?: boolean;
  /** Set the button alignment. OneOf('left', 'center', 'right'), Default: center */
  buttonAlign?: 'left' | 'center' |'right';
  /** Can incude a ErrorMessage.Action.# */
  children?: ReactNode;
  /** Display bullets next to each error. */
  useBullets?: boolean;
  /** Custom icon. */
  CustomIcon?: ComponentType<any>;
  /** Custom message type. */
  CustomMessage?: ComponentType<any>;
  /** Custom icon color. */
  iconColor?: string;
}

const ErrorMessageDefaultProps: {buttonAlign: 'left' | 'center' | 'right'} = {
  buttonAlign: 'center',
};

export type ErrorMessageStatics = {
  Content: typeof Content;
  Action: typeof Action;
};

const ErrorMessage: FC<ErrorMessageProps> & ErrorMessageStatics = ({
  children, errorTitle, error, forbidden, buttonAlign, useBullets, CustomIcon, CustomMessage, iconColor, ...props
}) => {
  const content = useMemo(() => excludeChildrenByType(children, Action), [children]);
  const action = useMemo(() => findChildByType(children, Action), [children]);

  const listElement = useMemo(() => useBullets ? 'ul' : 'div', [useBullets]);
  const itemElement = useMemo(() => useBullets ? 'li' : 'div', [useBullets]);

  const theme = useContext(ThemeContext);
  const errorMessage = useMemo(() => {
    if (typeof error === 'string') {
      return error;
    }

    if (Array.isArray(error)) {
      return (
        <StyledMessageList as={listElement}>
          {error.map((message, index) => (
            <StyledMessageItem
              as={itemElement}
              key={index}
              useBullets={useBullets}
            >
              {message}
            </StyledMessageItem>
          ))}
        </StyledMessageList>
      );
    }

    if (typeof error === 'object' && error !== null && 'message' in error) {
      return error.message;
    }

    return content;
  }, [content, error, itemElement, listElement, useBullets]);

  const Icon = useMemo(() => {
    if (CustomIcon) {
      return CustomIcon;
    }

    if (forbidden) {
      return NotInterestedIcon;
    }

    return theme?.icons?.error;
  }, [CustomIcon, forbidden, theme?.icons?.error]);

  return (
    <StyledMessage
      role="alert"
      messageTitle={errorTitle}
      iconColor={iconColor || theme.colors.error}
      Icon={Icon}
      as={CustomMessage}
      {...props}
    >
      <StyledText>{errorMessage}</StyledText>
      {action && <StyledActions align={buttonAlign}>{action}</StyledActions>}
    </StyledMessage>
  );
};

ErrorMessage.Content = Content;
ErrorMessage.Action = Action;
ErrorMessage.defaultProps = ErrorMessageDefaultProps;

export default ErrorMessage;
